import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import {
  clearAuthSessionStorage,
  getValidStoredSession,
  notifyAuthSessionChanged,
} from '../utils/authUtils';

const AuthContext = createContext();

export const useSimpleAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useSimpleAuth must be used within a SimpleAuthProvider');
  }
  return context;
};

export const SimpleAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const isCheckingRef = useRef(false);

  const applyGuestState = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const syncFromStorage = useCallback(() => {
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;

    try {
      const session = getValidStoredSession();
      if (session?.user) {
        setUser(session.user);
        setIsAuthenticated(true);
      } else {
        applyGuestState();
      }
    } catch (error) {
      console.error('SimpleAuthContext: syncFromStorage failed:', error);
      clearAuthSessionStorage();
      applyGuestState();
    } finally {
      setLoading(false);
      isCheckingRef.current = false;
    }
  }, [applyGuestState]);

  useEffect(() => {
    syncFromStorage();

    const handleAuthLogout = () => {
      applyGuestState();
      setLoading(false);
      syncFromStorage();
    };

    const handleAuthUpdate = () => {
      syncFromStorage();
    };

    const handleStorageChange = (e) => {
      if (
        e.key === 'authUser' ||
        e.key === 'authToken' ||
        e.key === 'fitconnect_token' ||
        e.key === 'fitconnect_user' ||
        e.key === 'adminToken' ||
        e.key === 'adminUserData'
      ) {
        syncFromStorage();
      }
    };

    window.addEventListener('authLogout', handleAuthLogout);
    window.addEventListener('authUpdate', handleAuthUpdate);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('authLogout', handleAuthLogout);
      window.removeEventListener('authUpdate', handleAuthUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [syncFromStorage, applyGuestState]);

  const login = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('authUser', JSON.stringify(userData));
    localStorage.setItem('fitconnect_user', JSON.stringify(userData));
    notifyAuthSessionChanged('login');
  };

  const logout = () => {
    clearAuthSessionStorage();
    applyGuestState();
    setLoading(false);
    notifyAuthSessionChanged('logout');
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{ background: 'linear-gradient(135deg, #232956 0%, #df2528 100%)' }}
      >
        <div className="text-center text-white">
          <div className="mb-4" style={{ fontSize: '4rem' }}>
            🏋️‍♂️
          </div>
          <div
            className="spinner-border text-light mb-3"
            role="status"
            style={{ width: '3rem', height: '3rem' }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <h4 className="mb-2">PT Thai Plus</h4>
          <p className="opacity-75">กำลังตรวจสอบการเข้าสู่ระบบ...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{ user, login, logout, isAuthenticated, loading, isLoading: loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
